import type { Modifier, ModifierArguments } from '@popperjs/core';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import clsx from 'clsx';
import {
  type Ref,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type MouseEvent as ReactMouseEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  ReactPortal,
} from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import type {
  ActionMeta,
  GroupBase,
  OnChangeValue,
  Options,
  MenuProps as ReactSelectMenuProps,
} from 'react-select';

import Borders from '@dynatrace/strato-design-tokens/borders';
import { CheckmarkSmallIcon, MinusIcon } from '@dynatrace/strato-icons';

import { Button } from '../../../buttons/button/Button.js';
import { ProgressCircle } from '../../../content/progress/ProgressCircle.js';
import { useId } from '../../../core/hooks/useId.js';
import { useMergeRefs } from '../../../core/hooks/useMergeRefs.js';
import { useOverlayStacking } from '../../../core/hooks/useOverlayStacking.js';
import { OverlayStackingProvider } from '../../../core/providers/OverlayStackingProvider.js';
import { Divider } from '../../../layouts/divider/Divider.js';
import { Flex } from '../../../layouts/flex/Flex.js';
import { Surface } from '../../../layouts/surface/Surface.js';
import { surfaceBorderRadius } from '../../../layouts/surface/variables.sty.js';
import { useOverlay } from '../../../overlays/hooks/useOverlay.js';
import { observeOverlaySize } from '../../../overlays/utils/popper-modifiers.js';
import { usePopper } from '../../../overlays/utils/use-popper-compat.js';
import { Text } from '../../../typography/text/Text.js';
import { _FormFieldContext as FormFieldContext } from '../../forms-core/contexts/FormFieldContext.js';
import { FormFieldMessageContext } from '../../forms-core/contexts/FormFieldMessageContext.js';
import { SearchInput } from '../../search-input/SearchInput.js';
import { useSelectMenuContext } from '../hooks/use-select-menu-context.js';
import { safeStringify } from '../hooks/use-select-options.js';
import { SelectActions } from '../state/select-menu-state-reducer.js';
import * as styles from '../styles/Select.sty.js';
import type { BaseOption } from '../types.js';

type MenuProps<
  OptionValue,
  isMulti extends boolean,
  Group extends GroupBase<OptionValue>,
> = ReactSelectMenuProps<OptionValue, isMulti, Group>;

// Popper modifier to sync the width of the overlay menu
const sameWidthModifier: Modifier<'sameWidth-as-trigger', object> = {
  name: 'sameWidth-as-trigger',
  enabled: true,
  fn: ({ state }: ModifierArguments<object>) => {
    state.styles.popper.width = `${state.rects.reference.width}px`;
    state.styles.popper.height = 'min-content';
  },
  phase: 'beforeWrite',
  requires: ['computeStyles'],
};

const heightModifier: Modifier<'select-height-handler', object> = {
  name: 'select-height-handler',
  enabled: true,
  fn: ({ state }: ModifierArguments<object>): void => {
    const viewportHeight = Math.max(
      document.documentElement.clientHeight || 0,
      window.innerHeight || 0,
    );

    const topOffset = state.rects.reference.y;
    const bottomOffset =
      viewportHeight - (state.rects.reference.y + state.rects.reference.height);
    const offset = state.options.placement.includes('bottom')
      ? bottomOffset
      : topOffset;

    if (offset < state.rects.popper.height) {
      // Limit the height to half the viewport height or 320px (which ever is greater),
      // subtract the height of the trigger and remove a 20 px buffer to the screen edge.
      const maxHeightBase = viewportHeight / 2 > 320 ? viewportHeight / 2 : 320;
      state.styles.popper.maxHeight = `${
        maxHeightBase - state.rects.reference.height - 20
      }px`;
    }
  },
  phase: 'beforeWrite',
  requires: ['computeStyles'],
};

export function Menu<
  OptionValue,
  isMulti extends boolean,
  Group extends GroupBase<OptionValue>,
>(props: MenuProps<OptionValue, isMulti, Group>): ReactPortal {
  const { children, innerRef, innerProps, selectProps } = props;
  const {
    filterRef,
    clearSelectionRef,
    triggerRef,
    inputValue,
    isMulti,
    onInputChange,
  } = selectProps;
  const {
    className: contentConsumerClassName,
    style: contentConsumerStyle,
    'data-testid': contentDataTestId,
    ...remainingContentProps
  } = selectProps.componentProps.content ?? {};
  const {
    className: filterConsumerClassName,
    style: filterConsumerStyle,
    'data-testid': filterDataTestId,
    ...remainingFilterProps
  } = selectProps.componentProps.filter ?? {};

  const [portalRef, setPortalRef] = useState<HTMLDivElement | null>(null);
  const [menuWidth, setMenuWidth] = useState<string | number | undefined>(
    triggerRef?.offsetWidth,
  );

  const rootPortalRef = useMergeRefs([innerRef, setPortalRef]);

  const stackingContext = useOverlayStacking(undefined, 'overlay');

  const { styles: positioningStyles } = usePopper(triggerRef, portalRef, {
    placement: 'bottom-start',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 4],
        },
      },
      sameWidthModifier,
      heightModifier,
      observeOverlaySize,
    ],
  });

  const { dispatch } = useSelectMenuContext();

  const clearFilterValue = useCallback(() => {
    if (inputValue !== '') {
      onInputChange('', {
        action: 'input-change',
        prevInputValue: inputValue,
      });
    }
  }, [inputValue, onInputChange]);

  useOverlay(
    {
      onClose: () => {
        dispatch({
          type: SelectActions.CLOSE,
        });
        clearFilterValue();
        setMenuWidth(triggerRef?.offsetWidth);
      },
      isOpen: selectProps.menuIsOpen,
      isDismissible: true,
      shouldCloseOnInteractOutside: (element) => {
        // close the menu when the click is outside of the select and the select trigger.
        // The closing when the trigger is clicked is handled by the trigger itself
        return !selectProps.triggerRef?.contains(element);
      },
    },
    innerRef as RefObject<HTMLElement>,
  );

  useEffect(() => {
    if (selectProps.menuIsOpen) {
      filterRef?.current?.inputRef?.focus({ preventScroll: true });
    }
  }, [filterRef, selectProps.menuIsOpen]);

  const hiddenTrigger = useMemo(
    () => selectProps.triggerRef?.querySelector('input'),
    [selectProps.triggerRef],
  );

  useEffect(() => {
    // Restore focus to the trigger when the menu unmounts, unless focus has
    // already moved to another element (e.g. a different Select's trigger).
    // When the portal unmounts after option selection or Escape, activeElement
    // falls back to document.body, so this correctly restores focus.
    return () => {
      if (!document.activeElement || document.activeElement === document.body) {
        hiddenTrigger?.focus();
      }
    };
  }, [hiddenTrigger]);

  const intl = useIntl();

  const ariaLabelId = useId();

  const selectableOptions = useMemo(
    () => (selectProps.allOptions ?? []).filter((option) => !option.isDisabled),
    [selectProps.allOptions],
  );

  const selectedKeys = useMemo(() => {
    const value = selectProps.value;
    const selected = Array.isArray(value)
      ? (value as BaseOption<OptionValue>[])
      : [];
    return new Set(selected.map((option) => safeStringify(option.value)));
  }, [selectProps.value]);

  const isOptionSelected = (option: BaseOption<OptionValue>) =>
    selectedKeys.has(safeStringify(option.value));

  const areAllOptionsSelected =
    isMulti &&
    selectableOptions.length > 0 &&
    selectableOptions.every(isOptionSelected);

  const areSomeOptionsSelected =
    isMulti && selectableOptions.some(isOptionSelected);

  let selectAllState: boolean | 'mixed' = false;
  if (areAllOptionsSelected) {
    selectAllState = true;
  } else if (areSomeOptionsSelected) {
    selectAllState = 'mixed';
  }

  const handleSelectAllInteraction = () => {
    if (!selectProps.loading) {
      if (areAllOptionsSelected) {
        selectProps.onChange([] as OnChangeValue<OptionValue, isMulti>, {
          action: 'clear',
          removedValues: selectProps.value as Options<OptionValue>,
        });
      } else {
        selectProps.onChange(
          selectableOptions as OnChangeValue<OptionValue, isMulti>,
          {} as ActionMeta<OptionValue>,
        );
      }
    }
  };

  const dataDtProperties = `state:${
    areAllOptionsSelected ? 'deselect' : 'select'
  }`;

  const noOptionsAvailable =
    selectProps.allOptions === undefined || selectProps.allOptions.length === 0;
  const isFiltering =
    selectProps.customFilter && inputValue !== undefined && inputValue !== '';
  const showClearSelection =
    selectProps.clearable &&
    selectProps.value &&
    isMulti === false &&
    !isFiltering;
  const showSelectAll =
    selectProps.clearable &&
    isMulti &&
    !isFiltering &&
    !noOptionsAvailable &&
    selectableOptions.length > 0;

  return createPortal(
    <Surface
      data-testid={contentDataTestId}
      data-dt-owner={selectProps.menuOwnerId}
      ref={rootPortalRef}
      {...(innerProps as unknown as Record<string, unknown>)}
      elevation="floating"
      padding={4}
      tabIndex={-1}
      className={clsx(
        styles.selectMenuOverlay({
          isTriggerWidth:
            selectProps.contentWidth === 'trigger' ||
            (selectProps.customFilter &&
              selectProps.contentWidth === 'content'),
        }),
        contentConsumerClassName,
      )}
      style={{
        zIndex: stackingContext.style.zIndex,
        ...positioningStyles.popper,
        width:
          selectProps.contentWidth !== 'trigger'
            ? selectProps.contentWidth
            : menuWidth,
        ...contentConsumerStyle,
        ...assignInlineVars({
          [surfaceBorderRadius]: Borders.Radius.Surface.Subdued,
        }),
      }}
      onMouseDown={(e: ReactMouseEvent<HTMLDivElement>) => e.stopPropagation()}
      {...remainingContentProps}
    >
      <OverlayStackingProvider>
        {selectProps.customFilter && (
          // The empty content is need so the TextInput does not consume the required coming from the FormField
          <FormFieldContext.Provider value={{}}>
            {/* The TextInput should not consume any validation context to not reflect error states. */}
            <FormFieldMessageContext.Provider
              value={{
                state: {
                  customMessages: [],
                  formControlMessages: [],
                  showError: false,
                },
              }}
            >
              <SearchInput
                data-testid={filterDataTestId}
                ref={filterRef}
                variant="minimal"
                aria-label={intl.formatMessage({
                  defaultMessage: 'Search available options',
                  id: '4PipZ2Z07Q9epib1',
                  description: 'Aria label for the filter inside the select',
                })}
                placeholder={intl.formatMessage({
                  defaultMessage: 'Search',
                  id: 'CerADEHqR/6IVmHr',
                  description: 'Placeholder for the filter inside the select',
                })}
                value={inputValue}
                onChange={(value: string) => {
                  onInputChange(value, {
                    action: 'input-change',
                    prevInputValue: inputValue,
                  });
                }}
                className={clsx(styles.filter, filterConsumerClassName)}
                style={filterConsumerStyle}
                {...remainingFilterProps}
              />
            </FormFieldMessageContext.Provider>
            <Divider
              className={clsx(
                styles.selectMenuDivider,
                'strato-select-divider',
              )}
              aria-hidden
            />
          </FormFieldContext.Provider>
        )}
        {showClearSelection && (
          <>
            <Button
              ref={clearSelectionRef as Ref<HTMLButtonElement>}
              width="full"
              textAlign="start"
              onClick={() => {
                selectProps.onChange(
                  null as OnChangeValue<OptionValue, isMulti>,
                  {
                    action: 'clear',
                    removedValues: selectProps.value as Options<OptionValue>,
                  },
                );
                // close the menu in single selection mode and clear the filter
                if (isMulti === false) {
                  dispatch({
                    type: SelectActions.CLOSE,
                  });
                  clearFilterValue();
                }
              }}
              onKeyDown={(event: ReactKeyboardEvent<HTMLButtonElement>) => {
                if (event.key === ' ' || event.key === 'Enter') {
                  selectProps.onChange(
                    null as OnChangeValue<OptionValue, isMulti>,
                    {
                      action: 'clear',
                      removedValues: selectProps.value as Options<OptionValue>,
                    },
                  );
                  // prevent option selection being triggered
                  event.preventDefault();
                  event.stopPropagation();

                  // close the menu in single selection mode and clear the filter
                  if (isMulti === false) {
                    dispatch({
                      type: SelectActions.CLOSE,
                    });
                    clearFilterValue();
                  }

                  // move the focus back to the trigger after clearing if possible
                  // otherwise move it back to the input
                  if (filterRef?.current && isMulti) {
                    filterRef.current.inputRef?.focus();
                  } else {
                    const hiddentrigger = triggerRef?.querySelector('input');
                    hiddentrigger?.focus();
                  }
                }
              }}
            >
              {intl.formatMessage({
                defaultMessage: 'Clear selection',
                id: '/H3rbOvaR6bAasVo',
                description: 'Select component clear selection button.',
              })}
            </Button>
            <Divider
              className={clsx(
                styles.selectMenuDivider,
                'strato-select-divider',
              )}
              aria-hidden
            />
          </>
        )}
        {showSelectAll && (
          <>
            <Flex
              tabIndex={0}
              role="checkbox"
              aria-checked={selectAllState}
              className={clsx(
                'strato-select-option',
                styles.selectOptionMulti,
                styles.selectAll,
                {
                  'strato-select-option--selected': areSomeOptionsSelected,
                  'strato-select-option--disabled': selectProps.loading,
                },
              )}
              ref={selectProps.clearSelectionRef as Ref<HTMLDivElement>}
              onClick={() => {
                handleSelectAllInteraction();
              }}
              onKeyDown={(event: ReactKeyboardEvent<HTMLDivElement>) => {
                if (event.key === ' ' || event.key === 'Enter') {
                  handleSelectAllInteraction();
                  event.preventDefault();
                  event.stopPropagation();
                }
              }}
              data-dt-children-name="SelectDeselectAll"
              data-dt-properties={dataDtProperties}
            >
              <div
                className={clsx(
                  styles.selectOptionMultiIndicator({
                    selected: areSomeOptionsSelected,
                  }),
                )}
              >
                {areAllOptionsSelected ? (
                  <CheckmarkSmallIcon
                    className={styles.selectOptionMultiIndicatorIcon}
                  />
                ) : null}
                {!areAllOptionsSelected && areSomeOptionsSelected ? (
                  <MinusIcon
                    className={styles.selectOptionMultiIndicatorIcon}
                  />
                ) : null}
              </div>
              {intl.formatMessage({
                defaultMessage: 'Select all',
                id: 'EsLvnx9UfUL/bZ/r',
                description: 'Select all option in the Select component.',
              })}
            </Flex>
            <Divider
              className={clsx(
                styles.selectMenuDivider,
                'strato-select-divider',
              )}
            />
          </>
        )}
        {selectProps.loading ? (
          <Flex
            paddingY={6}
            paddingLeft={8}
            paddingRight={12}
            className={styles.selectMenuLoadingWrapper}
          >
            <ProgressCircle size="small" aria-labelledby={ariaLabelId} />
            <Text id={ariaLabelId}>
              <FormattedMessage
                description="Label for the loading state."
                defaultMessage="Loading"
                id="cycz+bYG+VH7Of4e"
              />
            </Text>
          </Flex>
        ) : (
          children
        )}
      </OverlayStackingProvider>
    </Surface>,
    document.body,
  );
}
